import getSymbolFromCurrency from 'currency-symbol-map';
import moment from 'moment-timezone';

type DateValidationArgs = {
  startDate?: string;
  endDate?: string;
  numberOfSurveys?: number;
  timezone: string;
};

export const buildQueryParams = (filters: Record<string, any>) => {
  const params: Record<string, string> = {};

  Object.entries(filters).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;

    if (typeof value === 'boolean') {
      params[key] = value ? 'true' : 'false';
    } else {
      params[key] = String(value);
    }
  });

  return params;
};

export function sanitizeKey(key: string) {
  return key.toLowerCase().replace(/[–—]/g, '-').replace(/[’‘]/g, "'").replace(/\s+/g, '_');
}

export function generateEmojiList() {
  const ranges = [
    [0x1f600, 0x1f64f],
    [0x1f300, 0x1f5ff],
    [0x1f680, 0x1f6ff],
    [0x2600, 0x26ff],
    [0x2700, 0x27bf],
    [0x1f900, 0x1f9ff],
  ];

  const all: { symbol: string; code: string }[] = [];

  ranges.forEach(([start, end]) => {
    for (let code = start; code <= end; code++) {
      const char = String.fromCodePoint(code);
      if (/\p{Emoji}/u.test(char)) {
        all.push({
          symbol: char,
          code: 'U+' + code.toString(16).toUpperCase(),
        });
      }
    }
  });

  return all.sort(() => Math.random() - 0.5);
}

export function parseEmoji(input: string): string {
  if (!input) return '❔';

  const strictEmojiRegex = /\p{Extended_Pictographic}/u;

  // 1. If it's already an emoji, return it
  if (strictEmojiRegex.test(input)) {
    return input;
  }

  // 2. If it's in "U+XXXX" format
  if (input.startsWith('U+')) {
    const hex = input.replace('U+', '');
    const codePoint = parseInt(hex, 16);

    if (!isNaN(codePoint) && codePoint <= 0x10ffff) {
      try {
        return String.fromCodePoint(codePoint);
      } catch {
        return '❔';
      }
    }
  }

  // 3. Otherwise, it's not an emoji
  return '❔';
}

export const roleIdToRoleName: Record<number, string> = {
  1: 'SUPER_ADMIN',
  2: 'ADMIN',
  3: 'SURVEY_MANAGER',
  4: 'CONTENT_MODERATOR',
  5: 'VIEWER',
  6: 'MANAGER',
};

export function getChangedValues<T extends object>(original: T, updated: T): Partial<T> {
  const changed: Partial<T> = {};

  for (const key in updated) {
    const oldVal = original[key];
    const newVal = updated[key];

    if (JSON.stringify(oldVal) !== JSON.stringify(newVal)) {
      changed[key] = newVal;
    }
  }

  return changed;
}

export const extractFileNameFromUrl = (url: string): string => {
  try {
    const urlObj = new URL(url);
    const pathname = urlObj.pathname;
    // Remove leading slash and decode URI component
    const decodedPath = decodeURIComponent(pathname.substring(1));
    // Extract filename after the last slash
    const parts = decodedPath.split('/');
    const filenameWithTimestamp = parts[parts.length - 1];

    // Remove timestamp prefix (e.g., "1763383357957_sample_xls_form_1.xls" -> "sample_xls_form_1.xls")
    const match = filenameWithTimestamp.match(/^\d+_(.*)/);
    return match ? match[1] : filenameWithTimestamp;
  } catch (error) {
    console.error('Error extracting filename:', error);
    return 'Unknown file';
  }
};

export const formatDateForInput = (isoDate: string | null | undefined): string => {
  if (!isoDate) return '';
  return isoDate.split('T')[0]; // Extracts YYYY-MM-DD from ISO string
};

export const formatDate = (value: string) => {
  return new Date(value).toLocaleDateString('en-CA');
};

export function desanitizeKey(key: string): string {
  if (!key) {
    return '';
  }
  return key
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

export const formatTimestamp = (isoString: string): string => {
  if (!isoString) return '-';
  const date = new Date(isoString);

  const dateStr = date.toLocaleDateString('en-GB').replace(/\//g, '-');

  const timeStr = date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  });

  return `${dateStr} ${timeStr}`;
};

export const formatCurrency = (amount: number, currency: string, locale?: string): string => {
  try {
    const safeLocale = locale || (typeof navigator !== 'undefined' ? navigator.language : 'en-US');

    const upperCurrency = currency.toUpperCase();

    const symbol = getSymbolFromCurrency(upperCurrency) ?? upperCurrency;

    const formattedAmount = new Intl.NumberFormat(safeLocale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
    const nbsp = '\u00A0';
    return `${symbol}${nbsp}${formattedAmount}${nbsp}`;
  } catch {
    return `${currency?.toUpperCase()} ${amount?.toLocaleString()}`;
  }
};

type FileSystemWritableFileStream = WritableStream & {
  write: (data: any) => Promise<void>;
  close: () => Promise<void>;
};

interface FilePickerOptions {
  suggestedName?: string;
  types?: Array<{
    description: string;
    accept: Record<string, string[]>;
  }>;
}

interface SaveFilePicker {
  showSaveFilePicker(options?: FilePickerOptions): Promise<any>;
}

declare global {
  interface Window extends SaveFilePicker {}
}

export const saveFileWithPicker = async (blob: Blob, filename: string): Promise<string> => {
  const fileExtension = filename.split('.').pop() || 'file';

  // Checks for File System Access API
  const supportsPicker = 'showSaveFilePicker' in window;

  if (!supportsPicker) {
    // Fallback to normal download
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
    return filename;
  }

  try {
    // Use real save dialog
    const handle = await window.showSaveFilePicker({
      suggestedName: filename,
      types: [
        {
          description: `.${fileExtension} File`,
          accept: {
            [blob.type]: [`.${fileExtension}`],
          },
        },
      ],
    });

    const writable: FileSystemWritableFileStream = await handle.createWritable();
    await writable.write(blob);
    await writable.close();

    const finalFile = await handle.getFile();
    return finalFile.name;
  } catch (error: any) {
    // Check if user cancelled the save dialog
    if (error.name === 'AbortError' || error.message?.includes('aborted')) {
      // Silently handle user cancellation - don't throw error
      throw new Error('DOWNLOAD_CANCELLED');
    }
    // Re-throw other errors
    throw error;
  }
};

// function isAxiosError<T = any>(error: unknown): error is AxiosError<T> {
//   return (
//     typeof error === 'object' &&
//     error !== null &&
//     'isAxiosError' in error &&
//     (error as any).isAxiosError === true
//   );
// }

// export function handleApiError(error: unknown, fallbackMessage: string): Error {
//   if (isAxiosError<{ message?: string }>(error)) {
//     const message =
//       error.response?.data?.message || error.message || fallbackMessage;
//     return new Error(message);
//   }

//   const e = error as unknown;

//   if (e instanceof Error) {
//     return e;
//   }

//   return new Error(fallbackMessage);
// }

export const validateEndDateWithSurveys = (
  endDate: string,
  { startDate, numberOfSurveys, timezone }: DateValidationArgs,
) => {
  if (!startDate || !numberOfSurveys) return true;

  const start = moment.tz(startDate, timezone).startOf('day');
  const end = moment.tz(endDate, timezone).endOf('day');

  const currentDate = moment.tz(timezone).startOf('day');

  if (end.isBefore(currentDate)) return `End Date should not be in past`;

  const diffInDays = end.diff(start, 'days') + 1;
  if (end.isBefore(start)) {
    return 'End date must be after start date';
  }

  if (diffInDays < numberOfSurveys) return `Minimum ${numberOfSurveys} day(s) required.`;

  return true;
};

export const formatTargetAudience = (
  targetGroup: string,
  data?: {
    projectName?: string;
    siteName?: string;
    surveySetTitle?: string;
    posterCode?: string;
    fileName?: string;
    estimatedCount?: number;
  },
): string => {
  const baseText = desanitizeKey(targetGroup);

  switch (targetGroup) {
    case 'ALL_USERS':
      return `${baseText} - Send to all users on this platform`;

    case 'FILTER_USERS': {
      const filters = [];
      if (data?.projectName && data.projectName !== 'Choose project') {
        filters.push(`Project: ${data.projectName}`);
      }
      if (data?.siteName && data.siteName !== 'Choose Site') {
        filters.push(`Site: ${data.siteName}`);
      }
      if (data?.surveySetTitle && data.surveySetTitle !== 'Choose survey-set') {
        filters.push(`Survey Set: ${data.surveySetTitle}`);
      }
      if (data?.posterCode) {
        filters.push(`Poster Code: ${data.posterCode}`);
      }

      const filterText = filters.length > 0 ? filters.join(' | ') : 'No filters applied';
      const countText = data?.estimatedCount ? ` (${data.estimatedCount} users)` : '';
      return `${baseText} - ${filterText}${countText}`;
    }

    case 'BULK_USERS': {
      const fileName = data?.fileName ? ` - File: ${data.fileName}` : '';
      return `${baseText} - Upload a list of specific users to target${fileName}`;
    }

    default:
      return baseText || 'Target audience not specified';
  }
};
